import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { BillingService } from '../../../../core/services/billing.service';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
import { isStateModeGroup } from '../../../../shared/util/state-mode.util';
import { PatientBillingService } from '../../../../core/services/patient-billing.service';
import { PatientService } from '../../../../core/services/patient.service';
import { getCodeStateView } from '../../../../shared/util/code-state.util';
import { doesCodeMatchStatus } from '../../../../shared/util/code-status-filter.util';

@Component({
  selector: 'app-by-date-billing-list',
  templateUrl: './by-date-billing-list.component.html',
  styleUrls: ['./by-date-billing-list.component.scss']
})
export class ByDateBillingListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  @Input() statusFilters: string = '';
  @Input() dateRange: any = {};
  filteredPatients: any[] = [];
  allPatients: any[] = [];

  /** A state unit (tasks/state-mode.md) has no billing: no export, no money tooltips, no paid up front or pro bono labels. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private patientService: PatientService,
    private billingService: BillingService,
    private patientBillingService: PatientBillingService,
    private groupService: GroupService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.billingService.groupBilling$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.allPatients = results;
        this.applyFilter();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue'] || changes['statusFilters']) {
      this.applyFilter();
    }
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  getCodeTooltip(codeObj: any): string {
    let tooltip = `${codeObj.code} - ${codeObj.description}`;
    
    if (codeObj.paidCash) {
      tooltip += ' (Paid Up Front)';
    }
    
    if (codeObj.noCharge) {
      tooltip += ' (Pro Bono)';
    }
    
    return tooltip;
  }

  applyFilter() {
    let filteredData = this.allPatients;

    // Apply text filter first (existing logic)
    if (this.filterValue.trim() === '') {
      filteredData = this.groupAndSort(filteredData);
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      const textFilteredData = filteredData.filter(item => {

        const icdCodesMatch = item.Patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(term =>
          _.includes(item.Patient.name.toLowerCase(), term) ||
          _.includes(item.Patient.surname.toLowerCase(), term) ||
          _.includes(item.Patient.patientNumber?.toLowerCase(), term) ||
          _.includes(item.Patient.diagnosis?.toLowerCase(), term) ||
          _.includes(item.code?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      });
      filteredData = this.groupAndSort(textFilteredData);
    }

    // Apply status filter last (after all other filters)
    if (this.statusFilters) {
      this.filteredPatients = this.filterDateGroupsByStatus(filteredData, this.statusFilters);
    } else {
      // If no status filters are selected, show all patients (no filtering by status)
      this.filteredPatients = filteredData;
    }
  }

  /**
   * Filter date groups by status, filtering individual codes rather than entire patients
   * @param dateGroups - Array of date group objects
   * @param statusFilter - The status filter ('inpatient' or 'opd')
   * @returns Array of date groups with filtered patients and codes
   */
  private filterDateGroupsByStatus(dateGroups: any[], statusFilter: string): any[] {
    return dateGroups.map(dateGroup => {
      const filteredDateGroup = { ...dateGroup };
      
      // Filter patients within this date group
      filteredDateGroup.patients = dateGroup.patients.map((patient: any) => {
        const filteredPatient = { ...patient };
        
        // Filter the codes array to only include matching codes
        filteredPatient.codes = patient.codes?.filter((code: any) =>
          doesCodeMatchStatus(code, dateGroup.date, patient, statusFilter, this.dateRange)
        ) || [];

        // Update originalCodes to only include the filtered codes
        const filteredOriginalCodes = patient.originalCodes?.filter((code: any) =>
          doesCodeMatchStatus(code, dateGroup.date, patient, statusFilter, this.dateRange)
        ) || [];
        
        filteredPatient.originalCodes = filteredOriginalCodes;
        
        return filteredPatient;
      }).filter((patient: any) => patient.codes && patient.codes.length > 0); // Remove patients with no matching codes
      
      return filteredDateGroup;
    }).filter((dateGroup: any) => dateGroup.patients && dateGroup.patients.length > 0); // Remove empty date groups
  }

  /**
   * Check if the current date range includes today
   * @returns boolean indicating if today is within the date range
   */
  private isDateRangeIncludesToday(): boolean {
    if (!this.dateRange || !this.dateRange.startDate || !this.dateRange.endDate) {
      return false;
    }
    
    const today = moment().startOf('day');
    const startDate = moment(this.dateRange.startDate).startOf('day');
    const endDate = moment(this.dateRange.endDate).endOf('day');
    
    return today.isBetween(startDate, endDate, null, '[]');
  }

  private groupAndSort(data: any[]): any[] {
    // Group items by formatted date
    const groupedByDate = _.groupBy(data, item => {
      return item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD');
    });

    // Transform the grouped data into an array of objects
    const transformed = _.map(groupedByDate, (items, date) => {
      // Group items by patient ID
      const groupedByPatient = _.groupBy(items, 'Patient.id');

      // Transform the grouped patient data into an array of objects
      const patients = _.map(groupedByPatient, (patientItems) => {
        const patient = { ...patientItems[0].Patient };
        patient.originalCodes = patientItems;
        patient.codes = patientItems.map(item => ({
          code: item.code,
          description: item.description,
          paidCash: item.paidCash,
          noCharge: item.noCharge,
          provider: item.provider,
          id: item.id,
          status: item.status,
          source: item.source,
          connectClaimStatus: item.connectClaimStatus,
          connectSubmissionStatus: item.connectSubmissionStatus,
          connectLineStatus: item.connectLineStatus,
          connectBilledCents: item.connectBilledCents,
          connectApprovedCents: item.connectApprovedCents,
          connectRejectedCents: item.connectRejectedCents,
          connectPatientLiableCents: item.connectPatientLiableCents,
          connectPaidCents: item.connectPaidCents,
          connectClawbackCents: item.connectClawbackCents,
          connectClawbackAt: item.connectClawbackAt,
          connectProcedureCodeId: item.connectProcedureCodeId,
          state: getCodeStateView(item)
        }));
        patient.isGroupPaid = patientItems.filter(item => !item.paidCash).length !== 0;
        patient.isGroupNoCharge = patientItems.filter(item => !item.noCharge).length !== 0;
        return patient;
      });

      return { date, patients };
    });

    // Sort the transformed data by date in descending order
    return _.orderBy(transformed, 'date', 'desc');
  }

  togglePaidCash(event: MouseEvent, groupDate: any, codes: any[], paidCash: boolean) {
    event.stopPropagation();
    event.preventDefault();


    //const date: moment.Moment = moment.isMoment(groupDate) ? groupDate : moment(groupDate);

    const combinedData = {
      groupId: this.groupService.group.id,
      procedureCodes: codes,
      //date: date.utc().startOf('day').toISOString(),
      paidCash,
      noCharge: false
    };



    this.patientBillingService.saveCodeGroup(combinedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Procedure codes "Paid Up Front" status updated successfully');
        },
        error: (error) => {
          console.error('Error saving patient procedure codes updated: ', error);
          this.showToastMessage('There was an error marking the patient procedure codes as paid up front. Please try again.');
        }
      });
  }

  toggleNoCharge(event: MouseEvent, groupDate: any, codes: any[], noCharge: boolean) {
    event.stopPropagation();
    event.preventDefault();


    //const date: moment.Moment = moment.isMoment(groupDate) ? groupDate : moment(groupDate);

    const combinedData = {
      groupId: this.groupService.group.id,
      procedureCodes: codes,
      //date: date.utc().startOf('day').toISOString(),
      paidCash: false,
      noCharge
    };



    this.patientBillingService.saveCodeGroup(combinedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Procedure codes "Pro Bono" status updated successfully');
        },
        error: (error) => {
          console.error('Error saving patient procedure codes updated: ', error);
          this.showToastMessage('There was an error marking the patient procedure codes as pro bono. Please try again.');
        }
      });
  }


  navigateToPatientFile(patientId: string, event?: MouseEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  loadCodeGroup(event: MouseEvent, date: string, patientId: string, group: any) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['edit-procedure-code', date],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });

  }
}
